import styles from "./styles.module.css";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { useEffect, useMemo, useState } from "react";
import {
  buscarClienteSimples,
  buscarObservacoesClientes,
  cadastrarObservacoesClientes,
} from "../../../services/api-planejamento-tributario.service";
import Loading from "../../../components/Loading";
import { formatValue } from "../../../utils/formatValue";
import SnackBar from "../../../components/SnackBar";

const dadosInicial = {
  bairro: "",
  cep: "",
  cidade: "",
  cnpj: "",
  complemento: "",
  cpf: "",
  dtAbertura: "",
  email: "",
  estado: "",
  id: "",
  logradouro: "",
  nome: "",
  numero: "",
  telefone: "",
  tipo_documento: "",
  observacoes: [
    {
      id_usuario: 0,
      data_observacao: "1998-04-14",
      nome_usuario: "",
      texto_observacao: "",
    },
  ],
};

const ModalAnaliseSimples = ({ open, onClose, id }) => {
  const [dados, setDados] = useState(dadosInicial);
  const [loading, setLoading] = useState(false);
  const [modalObservacoes, setModalObservacoes] = useState({
    open: false,
    observacao: "",
  });
  const [openConfirm, setOpenConfirm] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const dadosCliente = await buscarClienteSimples(id);
        const responseObservacoes = await buscarObservacoesClientes(id);
        setDados({
          ...dadosCliente,
          observacoes:
            responseObservacoes.observacoes.length > 0
              ? responseObservacoes.observacoes
              : dadosInicial.observacoes,
        });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    open && fetch();
  }, [open]);

  const isCnpj = useMemo(() => {
    if (open && dados) {
      return dados?.tipo_documento === "cnpj" ? true : false;
    } else {
      return false;
    }
  }, [open, dados]);

  const handleClose = () => {
    setDados(dadosInicial);
    onClose();
  };

  const handleSnackBar = (obj) => {
    setObjSnackBar(obj);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setOpenConfirm(false);
      const body = {
        idCliente: id,
        observacao: modalObservacoes.observacao,
      };
      await cadastrarObservacoesClientes(body);
      const responseObservacoes = await buscarObservacoesClientes(id);
      setDados({
        ...dados,
        observacoes:
          responseObservacoes.observacoes ?? dadosInicial.observacoes,
      });
      handleSnackBar({
        type: "success",
        msg: "Observação adicionada com sucesso.",
      });
      setModalObservacoes({
        open: false,
        observacao: "",
      });
    } catch (error) {
      handleSnackBar({
        type: "error",
        msg: "Erro ao enviar observação. Contate o suporte.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle>
          <div className={styles.containerTitle}>
            <h3 className={styles.title}>
              <PersonIcon />
              Cadastro Simples
            </h3>
            <Button
              variant="outlined"
              onClick={() =>
                setModalObservacoes({ ...modalObservacoes, open: true })
              }
              className={styles.btnClose}
            >
              Adicionar Observação
            </Button>
          </div>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Loading loading={loading} />
          ) : (
            <Grid container spacing={2} sx={{ marginTop: ".2rem" }}>
              <Grid item lg={2} md={2} sm={6} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  name="tipo_documento"
                  value={
                    dados?.tipo_documento === "cnpj"
                      ? formatValue("cnpj", dados?.cnpj || "")
                      : formatValue("cpf", dados?.cpf || "")
                  }
                  label={dados?.tipo_documento?.toUpperCase() || ""}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={6} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  name="nome"
                  value={dados?.nome || ""}
                  label={
                    dados?.tipo_documento === "cnpj" ? "Razão Social" : "Nome"
                  }
                />
              </Grid>
              {isCnpj && (
                <Grid item lg={2} md={2} sm={6} xs={12}>
                  <TextField
                    size="small"
                    fullWidth
                    disabled
                    name="dtAbertura"
                    value={
                      dados?.dtAbertura
                        ? formatValue("data", dados?.dtAbertura)
                        : ""
                    }
                    label="Data de Abertura"
                  />
                </Grid>
              )}
              <Grid item lg={isCnpj ? 2 : 4} md={isCnpj ? 2 : 4} sm={6} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  name="cep"
                  value={formatValue("cep", dados?.cep || "")}
                  label="CEP"
                  disabled
                />
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  name="logradouro"
                  value={dados?.logradouro || ""}
                  label="Logradouro"
                />
              </Grid>
              <Grid item lg={2} md={2} sm={6} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  name="numero"
                  value={dados?.numero || ""}
                  label="Número"
                />
              </Grid>
              <Grid item lg={3} md={3} sm={6} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  name="complemento"
                  value={dados?.complemento || "-"}
                  label="Complemento"
                />
              </Grid>
              <Grid item lg={3} md={3} sm={6} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  name="bairro"
                  value={dados?.bairro || ""}
                  label="Bairro"
                />
              </Grid>
              <Grid item lg={3} md={3} sm={6} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  name="cidade"
                  value={dados?.cidade || ""}
                  label="Cidade"
                />
              </Grid>
              <Grid item lg={2} md={2} sm={6} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  name="estado"
                  value={dados?.estado || ""}
                  label="Estado"
                />
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  type="email"
                  name="email"
                  value={dados?.email || ""}
                  label="E-mail"
                />
              </Grid>
              <Grid item lg={3} md={3} sm={6} xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  name="telefone"
                  value={dados?.telefone || ""}
                  label="Telefone/ Celular"
                />
              </Grid>
            </Grid>
          )}
          {dados.observacoes[0].id_usuario !== 0 ? (
            <div className={styles.containerAccordion}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  Observações
                </AccordionSummary>
                {dados.observacoes.map((observacao, index) => {
                  return (
                    <AccordionDetails key={index}>
                      <div className={styles.detailAccordion}>
                        <div className={styles.detailAccordionHeader}>
                          <h4>{observacao.nome_usuario}</h4>
                          <p>
                            {formatValue("data", observacao.data_observacao)}
                          </p>
                        </div>
                        <p>{observacao.texto_observacao}</p>
                      </div>
                    </AccordionDetails>
                  );
                })}
              </Accordion>
            </div>
          ) : (
            ""
          )}
          <div className={styles.containerBtn}>
            <Button
              variant="outlined"
              onClick={handleClose}
              className={styles.btnClose}
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={modalObservacoes.open}
        onClose={() => setModalObservacoes({ open: false, observacao: "" })}
        fullWidth
        maxWidth="md"
      >
        <DialogContent>
          <div className={styles.titleObs}>
            <EditNoteIcon />
            <h3>Adicionar Observação</h3>
          </div>
          <br />
          {loading ? (
            <Loading loading={loading} />
          ) : (
            <>
              <TextField
                multiline
                rows={4}
                fullWidth
                label="Observações"
                value={modalObservacoes.observacao}
                onChange={(e) =>
                  setModalObservacoes({
                    ...modalObservacoes,
                    observacao: e.target.value,
                  })
                }
              />
              <div className={styles.containerBtnObs}>
                <Button
                  variant="outlined"
                  className={styles.btnClose}
                  onClick={() =>
                    setModalObservacoes({ open: false, observacao: "" })
                  }
                >
                  Fechar
                </Button>
                <Button
                  variant="contained"
                  className={styles.btnSend}
                  onClick={() => {
                    if (modalObservacoes.observacao !== "") {
                      setOpenConfirm(true);
                    } else {
                      setObjSnackBar({
                        type: "error",
                        msg: "Campo de observações vazio.",
                      });
                    }
                  }}
                >
                  Enviar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogContent>
          <div className={styles.titleObs}>
            <EditNoteIcon />
            <h3>Confirme a observação</h3>
          </div>
          <br />
          <p style={{ textAlign: "center" }}>
            Deseja mesmo adicionar uma observação no cadastro de{" "}
            <strong>{dados?.nome}</strong>?
          </p>
          <div className={styles.containerBtnObs}>
            <Button
              variant="outlined"
              className={styles.btnClose}
              onClick={() => setOpenConfirm(false)}
            >
              não
            </Button>
            <Button
              variant="contained"
              className={styles.btnSend}
              onClick={handleSubmit}
            >
              sim
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={handleSnackBar} />
    </>
  );
};

export default ModalAnaliseSimples;
